import { trans as translate } from '../../util/transHelpers';

const ERROR = {
	EVENT_ENDED_BUT_NO_DVR_OR_VOD_AVAILABLE: 'error_event_ended_but_no_dvr_or_vod_available',
	EVENT_ENDED_BUT_NO_STREAM_WAS_AVAILABLE: 'error_event_ended_but_no_stream_was_available',
	EVENT_STARTED_BUT_NO_STREAM_AVAILABLE:   'error_event_started_but_no_stream_available',
	NO_STREAM_AVAILABLE:                     'error_no_stream_available',
	EVENT_WAS_MADE_PRIVATE:                  'error_event_was_made_private',
	EVENT_VOD_PROCESSING:                    'error_event_vod_processing',
	EVENT_VOD_FAILURE:                       'error_event_vod_failure',
	EVENT_HAS_PLACEHOLDER:                   'error_event_placeholder',
	EVENT_PAYMENT_REQUIRED:                  'error_event_payment_required',
	EVENT_PLAYOUT_FAILED:                    'error_event_playout_failed',
	EVENT_GEOBLOCKED:                        'error_event_geoblocked',
	PARALLEL_STREAM_DETECTED:                'error_parallel_stream_detected',
	EVENT_IS_LIVE_BUT_STREAM_FAILED:         'error_event_is_live_but_stream_failed',
};

const PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY = 'exception.player.error_please_wait';

const errorMap = {
	// Only for tagging tool
	[ERROR.NO_STREAM_AVAILABLE]: {
		context:  'tagging-tool',
		priority: 0,
		text:     {
			title:    'exception.player.error_no_stream_available',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},
		match: ({ event }) => {
			const isLiveEvent = !event.vodOnly;
			const hasStream = (event.streams ?? []).some((stream) => Boolean(stream.startedAt));
			const videoAvailable = event.videoAvailable;
			return isLiveEvent && !hasStream && !videoAvailable;
		},
	},
	[ERROR.EVENT_STARTED_BUT_NO_STREAM_AVAILABLE]: {
		priority: 0,
		text:     {
			title:    'exception.player.error_event_started_but_no_stream_available',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},
		match: ({ event, currentTime }) => {
			const isLiveEvent = !event.vodOnly;
			const hasStream = (event.streams ?? []).some((stream) => Boolean(stream.startedAt));
			const isLiveRightNow = event.startTimestamp <= currentTime && currentTime <= event.endTimestamp;
			return isLiveEvent && !hasStream && isLiveRightNow;
		},
	},
	[ERROR.EVENT_ENDED_BUT_NO_STREAM_WAS_AVAILABLE]: {
		priority: 0,
		text:     {
			title: 'exception.player.error_event_ended_but_no_stream_was_available',
		},
		match: ({ event, currentTime }) => {
			const isLiveEvent = !event.vodOnly;
			const hasStreamStarted = (event.streams ?? []).some((stream) => Boolean(stream.startedAt));
			const videoAvailable = event.videoAvailable;
			const hasEnded = currentTime > event.endTimestamp;
			return isLiveEvent && !hasStreamStarted && hasEnded && !videoAvailable;
		},
	},
	[ERROR.EVENT_VOD_PROCESSING]: {
		priority: 0,
		text:     {
			title:    'exception.player.error_event_vod_processing',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},
		match: ({ event }) => {
			return !event.videoAvailable && event.videoProcessing;
		},
	},
	[ERROR.EVENT_ENDED_BUT_NO_DVR_OR_VOD_AVAILABLE]: {
		priority: 0,
		text:     {
			title:    'exception.player.error_event_ended_but_no_dvr_or_vod_available',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},

		match: ({ event, currentTime }) => {
			const isLiveEvent = !event.vodOnly;
			const videoAvailable = event.videoAvailable;
			const hasEnded = currentTime > event.endTimestamp;
			return isLiveEvent && !videoAvailable && hasEnded;
		},
	},
	[ERROR.EVENT_VOD_FAILURE]: {
		priority: 0,
		text:     {
			title: 'exception.player.error_event_vod_failure',
		},

		match: ({ event, currentTime }) => {
			const hasEnded = currentTime > event.endTimestamp;
			const videoAvailable = event.videoAvailable;
			return !videoAvailable && hasEnded;
		},
	},
	[ERROR.EVENT_PLAYOUT_FAILED]: {
		priority: 0,
		text:     {
			title: 'exception.player.error_event_playout_failed',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.EVENT_PLAYOUT_FAILED;
		},
	},
	[ERROR.EVENT_WAS_MADE_PRIVATE]: {
		priority: 1,
		text:     {
			title: 'exception.player.error_event_was_made_private',
		},
		match: ({ event }) => Boolean(event.wasMadePrivate),
	},
	[ERROR.EVENT_HAS_PLACEHOLDER]: {
		ignoreInContext: ['tagging-tool'],
		priority:        2,
		text:            {
			title: ({ event }) => {
				const placeholder = JSON.parse(event.placeholderText);
				return placeholder?.[$arenaConfig.currentLanguageKey] || placeholder?.[Object.keys(placeholder)[0]];
			},
		},
		match: ({ event }) => {
			// check if location has disablePlaceholderScreen. If so, return false
			if (window.location.search.includes('disablePlaceholderScreen')) {
				return false;
			}
			return Boolean(event.placeholder);
		},
	},
	[ERROR.EVENT_PAYMENT_REQUIRED]: {
		priority: 3,
		text:     {
			title: 'exception.player.error_event_payment_required',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.EVENT_PAYMENT_REQUIRED;
		},
	},
	[ERROR.EVENT_GEOBLOCKED]: {
		priority: 4,
		text:     {
			title: 'exception.player.error_event_geoblocked',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.EVENT_GEOBLOCKED;
		},
	},
	[ERROR.PARALLEL_STREAM_DETECTED]: {
		priority: 5,
		text:     {
			title: 'exception.player.error_parallel_stream_detected',
		},
		match: ({ playoutError }) => {
			return playoutError === ERROR.PARALLEL_STREAM_DETECTED;
		},
	},
	[ERROR.EVENT_IS_LIVE_BUT_STREAM_FAILED]: {
		context:  'cockpit',
		priority: 0,
		text:     {
			title:    'exception.player.error_event_is_live_but_stream_failed',
			subtitle: PLEASE_WAIT_SUBTITLE_TRANSLATION_KEY,
		},
		match: ({ event, playerError, currentTime }) => {
			const isLiveEvent = !event.vodOnly;
			const hasStream = (event.streams || []).some((stream) => Boolean(stream.startedAt));
			const isLiveRightNow = event.startTimestamp <= currentTime && currentTime <= event.endTimestamp;
			const hasPlayerError = Boolean(playerError);
			return isLiveEvent && hasStream && isLiveRightNow && hasPlayerError;
		},
	},
};

class ErrorScreen {
	errorKey = null;
	event = null;
	currentTime = null;
	config = null;
	context = null;

	constructor({ errorKey, event, currentTime, config, context }) {
		if (errorKey !== null && !errorMap[errorKey]) {
			throw new Error(`Unknown error key: ${errorKey}`);
		}
		this.errorKey = errorKey;
		this.event = event;
		this.currentTime = currentTime;
		this.config = config;
		this.context = context;
	}

	static fromContext({ event, playerError, playoutError, currentTime, context }) {
		const error = Object.entries(errorMap).reduce(
			(primaryError, [key, config]) => {
				const ignore = config.ignoreInContext && config.ignoreInContext.includes(context);
				if (
					(primaryError.key === null || primaryError.config.priority < config.priority) &&
					((context && config.context === context) || !config.context) && !ignore &&
					config.match({
						event,
						playerError,
						playoutError,
						currentTime,
					})
				) {
					return {
						key,
						config,
					};
				}
				return primaryError;
			},
			{ key: null, config: {} }
		);
		if (error.key) {
			return new ErrorScreen({ errorKey: error.key, event, currentTime, config: error.config, context });
		}
		return null;
	}

	get error() {
		if (!this.errorKey) {
			return null;
		}
		return errorMap[this.errorKey];
	}

	get title() {
		if (!this.error) {
			return null;
		}
		if (typeof this.error.text.title === 'function') {
			return this.error.text.title({ event: this.event });
		}
		return translate(this.error.text.title);
	}

	get subtitle() {
		if (!this.error || !this.error.text.subtitle) {
			return null;
		}
		return translate(this.error.text.subtitle);
	}
}

export default ErrorScreen;
export { ErrorScreen, ERROR };
